import { ModsPanelCompProps } from "../../ModsPanel";
import {
  COMMON_PATHS,
  findLayersOrLayerAssets,
  getPath,
  IInteractiveModConcat,
  interactiveConcatJumpTypes,
} from "@blings/blings-player";
import { AddInteractiveHOC } from "./InteractiveFormHOC";
import { useMst } from "../../../../stores/main";
import { Button, Col, Input, InputNumber, Row, Select, Space } from "antd";
import { JsEditor } from "../../../Editor/JsEditor";
import { useEffect, useState } from "react";
import MainCTAButton from "../../../MainCTA/MainCTAButton";
import { ExpressionIcon } from "../../../../icons-and-animations/Icons";
import {
  HoverTooltip,
  InfoTooltip,
} from "../../../../BaseComps/InfoTooltip";
import {
  sharedInputAllowClear,
  sharedSelectAllowClear,
} from "../../../antd-extensions/selectClearIcon";

const { Option, OptGroup } = Select;

const InteractiveConcatRender = (
  props: ModsPanelCompProps<IInteractiveModConcat>
) => {
  const {
    platformStore,
    accountStore: { isBlingsAccount },
  } = useMst();
  const { change } = props;

  const [useCode, setUseCode] = useState(props.change.functionString != null);
  const [prevState, setPrevState] = useState<{
    value?: Array<string | { lineup: string } | { nodeId: string }>;
    jumpType?: interactiveConcatJumpTypes;
    jumpValue?: IInteractiveModConcat["jumpValue"];
  } | null>(null);
  const defaultJumpsValue = {
    sceneStart: (() => {
      const firstValue = props.change.value && props.change.value[0];
      // For scenes (strings), use directly. For flows/nodes (objects), use the object directly
      if (firstValue && typeof firstValue === "string") {
        return firstValue;
      } else if (
        firstValue &&
        typeof firstValue === "object" &&
        ("lineup" in firstValue || "nodeId" in firstValue)
      ) {
        return firstValue; // Use the actual object, not stringified
      }
      return undefined;
    })(),
    sceneEnd: (() => {
      const firstValue = props.change.value && props.change.value[0];
      // For scenes (strings), use directly. For flows/nodes (objects), use the object directly
      if (firstValue && typeof firstValue === "string") {
        return firstValue;
      } else if (
        firstValue &&
        typeof firstValue === "object" &&
        ("lineup" in firstValue || "nodeId" in firstValue)
      ) {
        return firstValue; // Use the actual object, not stringified
      }
      return undefined;
    })(),
    frame: 1,
    currentFrame: undefined,
  };

  const { json, index, onChange } = props;

  useEffect(() => {
    if (change.ctaName == null) {
      const layers = findLayersOrLayerAssets({
        assetId: change.assetId,
        layerName: change.layerName,
        layerUid: change.layerUid,
        jsonVid: json,
        additionals: change.additionals,
      });
      const layer =
        layers?.length &&
        layers.find((layer) => getPath(layer, COMMON_PATHS.TEXT_IN_LAYER));
      if (layer) {
        const text = getPath(layer, COMMON_PATHS.TEXT_IN_LAYER);
        text && onChange(index, "ctaName", text);
      }
    }
  }, [
    change.layerName,
    change.additionals,
    index,
    json,
    change.ctaName,
    change.assetId,
    change.layerUid,
    onChange,
  ]);

  const jumpValueRender = () => {
    switch (props.change.jumpType) {
      case "sceneStart":
      case "sceneEnd":
        return (
          <Select
            style={{ minWidth: "200px", width: "100%" }}
            value={(() => {
              // Handle string, lineup objects, and node objects for display
              const jumpValue = props.change.jumpValue;
              if (typeof jumpValue === "string") {
                return jumpValue;
              } else if (
                jumpValue &&
                typeof jumpValue === "object" &&
                ("lineup" in jumpValue || "nodeId" in jumpValue)
              ) {
                return JSON.stringify(jumpValue);
              }
              return jumpValue;
            })()}
            onChange={(v) => {
              // Parse the value back to object if it's a flow or node
              let actualValue = v;
              try {
                // Only try to parse if it's a string
                if (typeof v === "string") {
                  const parsed = JSON.parse(v);
                  if (
                    parsed &&
                    typeof parsed === "object" &&
                    ("lineup" in parsed || "nodeId" in parsed)
                  ) {
                    actualValue = parsed; // Use actual object for flows/nodes
                  }
                }
              } catch (e) {
                // Keep as original value for scenes or invalid JSON
              }
              props.onChange(props.index, ["jumpValue"], [actualValue]);
            }}
          >
            {props.change.value?.map((item) => {
              // Handle scenes (strings), flows, and nodes (objects)
              if (typeof item === "string") {
                // Scene
                return (
                  <Option key={item} value={item}>
                    {item}
                  </Option>
                );
              } else if (item && typeof item === "object" && "lineup" in item) {
                // Flow - use stringified object as value for Select component, but store actual object
                const flowItem = item as { lineup: string };
                return (
                  <Option key={flowItem.lineup} value={JSON.stringify(item)}>
                    {flowItem.lineup} (Flow)
                  </Option>
                );
              } else if (item && typeof item === "object" && "nodeId" in item) {
                // Node - use stringified object as value for Select component, but store actual object
                const nodeItem = item as { nodeId: string };
                return (
                  <Option key={nodeItem.nodeId} value={JSON.stringify(item)}>
                    {nodeItem.nodeId} (Node)
                  </Option>
                );
              }
              return null;
            })}
          </Select>
        );
      case "frame":
        return (
          <InputNumber
            value={props.change.jumpValue as number}
            onChange={(value) =>
              props.onChange(props.index, ["jumpValue"], [value])
            }
          />
        );
      default:
        return <></>;
    }
  };
  return (
    <Space
      direction={"vertical"}
      size={12}
      style={{ width: "100%" }}
      className="interactive-space"
    >
      <Row>
        <Col style={{ width: "100%" }}>
          <span style={{ color: "#6E7079", lineHeight: "22px" }}>
            Name this button (Required for analytics)
          </span>
          <InfoTooltip
            style={{ marginLeft: "5px" }}
            info="This name is used to track how many viewers clicked on the button."
          />
          <Input
            style={{ width: "100%", marginTop: "4px" }}
            id="connector-cta-name"
            value={props.change.ctaName}
            // eslint-disable-next-line no-template-curly-in-string
            placeholder="Name this button"
            onChange={(e) =>
              props.onChange(props.index, ["ctaName"], [e.target.value])
            }
            allowClear={sharedInputAllowClear}
          />
        </Col>
      </Row>
      {useCode ? (
        <Col>
          <Row justify="end">
            <HoverTooltip
              title="Use Selector"
              description="Return to choosing scenes and flows from the menu instead of custom code."
              immediate
            >
              <Button
                className="active-code-icon"
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() => {
                  setUseCode(false);
                  props.onChange(
                    props.index,
                    ["functionString", "value", "jumpType", "jumpValue"],
                    !prevState
                      ? [undefined, [], "sceneStart", undefined]
                      : [
                          undefined,
                          prevState.value,
                          prevState.jumpType,
                          prevState.jumpValue,
                        ]
                  );
                }}
                aria-label="Use scene and flow selector"
              >
                <ExpressionIcon />
              </Button>
            </HoverTooltip>
          </Row>
          <JsEditor
            defaultValue={props.change.functionString}
            onChange={(e: any) =>
              props.onChange(props.index, "functionString", e)
            }
            runtimeContext="interactiveConcat"
            style={{ marginTop: "-1.1rem" }}
            tooltipText="This function will will determine the new timeline when the user inteacts with the layer/s and update the data accordingly."
          />
        </Col>
      ) : (
        <div className="interactive-destination-field">
          <div className="interactive-destination-field__label">
            <span>Destination scenes or flows</span>
            <InfoTooltip
              info="Choose where this interaction sends the viewer. Select one or more scenes from this project, or choose a Flow: a named path from the Flow Map that you can edit in the Flow Map editor from the top bar."
              style={{ marginLeft: "5px" }}
            />
          </div>
          <Row>
            <Select
              mode="multiple"
              allowClear={sharedSelectAllowClear}
              className="dynamic-scene-select blings-select-controls"
              style={{ flexGrow: "1", marginRight: "0.3rem", maxWidth: "85%" }}
              placeholder={
                isBlingsAccount
                  ? "Select scenes, flows, or nodes"
                  : "Select scenes or flows"
              }
              value={props.change.value?.map((scene) =>
                typeof scene === "string" ? scene : JSON.stringify(scene)
              )}
              onChange={(value) => {
                const values = value.map((v) => {
                  try {
                    return JSON.parse(v);
                  } catch (e) {}
                  return v;
                });
                props.onChange(props.index, ["value"], [values]);

                // Set jumpValue with appropriate format: strings for scenes, actual objects for flows/nodes
                const firstValue = values[0];
                if (firstValue && typeof firstValue === "string") {
                  // Scene - use string directly
                  props.onChange(props.index, "jumpValue", firstValue);
                } else if (
                  firstValue &&
                  typeof firstValue === "object" &&
                  ("lineup" in firstValue || "nodeId" in firstValue)
                ) {
                  // Flow or Node - use actual object (not stringified)
                  props.onChange(props.index, "jumpValue", firstValue);
                } else {
                  // Clear jumpValue if no valid selection
                  props.onChange(props.index, "jumpValue", undefined);
                }
              }}
            >
              {platformStore.lineups.length ||
              (isBlingsAccount && platformStore.nodeIds.length) ? (
                <>
                  {platformStore.lineups.length > 0 && (
                    <OptGroup key="Flows">
                      {platformStore.lineups.map((lineup) => (
                        <Option key={lineup} value={JSON.stringify({ lineup })}>
                          {lineup}
                        </Option>
                      ))}
                    </OptGroup>
                  )}
                  <OptGroup key="Scenes">
                    {platformStore.projectWorkspaceVersion?.videoParts?.map(
                      (vp) => (
                        <Option key={vp.name} value={vp.name}>
                          {vp.name}
                        </Option>
                      )
                    )}
                  </OptGroup>
                  {isBlingsAccount && platformStore.nodeIds.length > 0 && (
                    <OptGroup key="Nodes">
                      {platformStore.nodeIds.map((nodeId) => (
                        <Option key={nodeId} value={JSON.stringify({ nodeId })}>
                          {nodeId}
                        </Option>
                      ))}
                    </OptGroup>
                  )}
                </>
              ) : (
                platformStore.projectWorkspaceVersion?.videoParts?.map((vp) => (
                  <Option key={vp.name} value={vp.name}>
                    {vp.name}
                  </Option>
                ))
              )}
            </Select>
            <HoverTooltip
              title="Use Code"
              description="Switch to custom JavaScript for deciding which scenes or flows should play next."
              immediate
            >
              <Button
                className="open-code-icon"
                style={{
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={() => {
                  setUseCode(true);
                  setPrevState({
                    jumpType: props.change.jumpType,
                    jumpValue: props.change.jumpValue,
                    value: props.change.value,
                  });
                  const functionString = functionStringFromState(
                    props,
                    platformStore.isControlDeprecated
                  );
                  props.onChange(
                    props.index,
                    ["functionString", "value", "jumpType", "jumpValue"],
                    [functionString, [], undefined, undefined]
                  );
                }}
                aria-label="Use code"
              >
                <ExpressionIcon />
              </Button>
            </HoverTooltip>
          </Row>
        </div>
      )}

      {!useCode && props.change.value?.length ? (
        <Space style={{ width: "100%" }}>
          <label>
            Goto:
            <Select
              value={props.change.jumpType}
              onChange={(v) => {
                props.onChange(
                  props.index,
                  ["jumpType", "jumpValue"],
                  [v, defaultJumpsValue[v]]
                );
              }}
            >
              <Option value="sceneStart">Scene Start</Option>
              <Option value="sceneEnd">Scene End</Option>
              <Option value="frame">Specific Frame</Option>
              <Option value="currentFrame">Current Frame</Option>
            </Select>
          </label>
          {jumpValueRender()}
        </Space>
      ) : null}
      <MainCTAButton
        change={props.change}
        onChange={props.onChange}
        index={props.index}
      />
    </Space>
  );
};

export function AddInteractiveConcatForm(
  props: ModsPanelCompProps<IInteractiveModConcat>
) {
  return (
    <AddInteractiveHOC<IInteractiveModConcat>
      {...props}
      renderSpecificInteractive={InteractiveConcatRender}
    />
  );
}
function functionStringFromState(
  props: ModsPanelCompProps<IInteractiveModConcat>,
  controlDeprecated = false
) {
  const jumpType =
    props.change.jumpType && props.change.jumpType in interactiveConcatJumpTypes
      ? props.change.jumpType
      : "sceneStart";
  return `async ({ data,${
    controlDeprecated ? "" : "liveControl,"
  } inputs, element, event, player, jumpTypes }) => {
  //data.oneOfTheParams = "new value"
  return {
    // required. pre fill with the non-advanced config, when switching to advanced
    // notice - this enables the functionality of selecting next scenes with logic,
    scenes: [${props.change.value
      ?.map((v) => {
        if (typeof v === "string") {
          return `"${v}"`;
        } else if (
          v &&
          typeof v === "object" &&
          ("lineup" in v || "nodeId" in v)
        ) {
          return JSON.stringify(v);
        }
        return `"${v}"`;
      })
      .join(", ")}],

    // both optional. pre fill with the non-advanced config, when switching to advanced
    jumpType: jumpTypes.${jumpType},
    jumpValue: ${
      typeof props.change.jumpValue === "string"
        ? `"${props.change.jumpValue}"`
        : typeof props.change.jumpValue === "object"
        ? JSON.stringify(props.change.jumpValue)
        : props.change.jumpValue
    },

    // optional, if provided, update the data accodringly. 
    data
  }
}
  `;
}
